import { useState, useEffect, useCallback } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchAssetMedia, fetchAssetMediaDownload } from '../services/api/assetMedia';

export default function useAssetMediaPublic(accessKey, fieldEngineerId) {
  const isMounted = useIsMounted();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openError, setOpenError] = useState(null);

  const fetchMedia = useCallback(async () => {
    if (!accessKey || !fieldEngineerId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAssetMedia(accessKey, fieldEngineerId);
      if (!isMounted.current) return;
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to load documents');
      setItems([]);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [accessKey, fieldEngineerId, isMounted]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const openDocument = useCallback(async (mediaId) => {
    if (!accessKey || !fieldEngineerId || !mediaId) return;

    setOpenError(null);
    try {
      const { url, filename } = await fetchAssetMediaDownload(accessKey, mediaId, fieldEngineerId);
      if (!isMounted.current) return;
      if (!url) {
        throw new Error('Download link unavailable');
      }

      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      if (!isMounted.current) return;
      console.error('Failed to open document:', err);
      const body = err.body;
      const message =
        (Array.isArray(body?.errors) && body.errors.join(', ')) ||
        body?.error ||
        err.message ||
        'Could not open document';
      setOpenError(message);
    }
  }, [accessKey, fieldEngineerId, isMounted]);

  return { items, loading, error, openError, openDocument, refresh: fetchMedia };
}
